import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, MessageSquarePlus, Search, Loader2, User, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clientsService } from '@/services/clients.service';
import { chatService, Conversation } from '@/services/chat.service';
import { Button, useToast } from '@caruso/ui';
import { cn } from '@caruso/ui';

interface NewConversationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConversationCreated: (conversation: Conversation) => void;
}

export function NewConversationDialog({
  isOpen,
  onClose,
  onConversationCreated,
}: NewConversationDialogProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const { toast } = useToast();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch clients
  const { data: clientsData, isLoading: isLoadingClients } = useQuery({
    queryKey: ['clients', debouncedSearch],
    queryFn: () => clientsService.getAll({ search: debouncedSearch, pageSize: 20 }),
    enabled: isOpen,
  });

  const clients = clientsData?.data || [];

  // Reset form when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedClientId(null);
    }
  }, [isOpen]);

  const handleStartConversation = async () => {
    if (!selectedClientId) {
      toast({
        title: 'Selecione um cliente',
        description: 'E necessario selecionar um cliente para iniciar a conversa.',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      const conversation = await chatService.startConversation(selectedClientId);
      toast({
        title: 'Conversa iniciada',
        description: 'A conversa foi criada com sucesso.',
        variant: 'success',
      });
      onConversationCreated(conversation);
      onClose();
    } catch (error: any) {
      console.error('Error starting conversation:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Nao foi possivel iniciar a conversa.',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const selectedClient = clients.find((c: any) => c.id === selectedClientId);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-brand-ink border border-brand-gold/20 rounded-xl w-full max-w-lg shadow-2xl max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-brand-gold/10">
                <div className="flex items-center gap-2">
                  <MessageSquarePlus className="w-5 h-5 text-brand-gold" />
                  <h2 className="text-lg font-serif text-brand-gold">Nova Conversa</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-lg hover:bg-brand-gold/10 text-brand-ivory/50 hover:text-brand-gold transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search */}
              <div className="p-4 border-b border-brand-gold/10">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-ivory/50" />
                  <input
                    type="text"
                    placeholder="Buscar cliente por nome, telefone ou email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={cn(
                      'w-full pl-10 pr-4 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg',
                      'text-brand-ivory placeholder:text-brand-ivory/40',
                      'focus:outline-none focus:border-brand-gold/30 text-sm'
                    )}
                    autoFocus
                  />
                </div>
              </div>

              {/* Client List */}
              <div className="flex-1 overflow-y-auto p-2 min-h-[200px] max-h-[300px]">
                {isLoadingClients ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-6 h-6 text-brand-gold animate-spin" />
                  </div>
                ) : clients.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-brand-ivory/50 text-sm">
                    <User className="w-8 h-8 mb-2 opacity-50" />
                    <p>{search ? 'Nenhum cliente encontrado' : 'Digite para buscar clientes'}</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {clients.map((client: any) => (
                      <button
                        key={client.id}
                        onClick={() => setSelectedClientId(client.id)}
                        disabled={!client.phone}
                        className={cn(
                          'w-full p-3 rounded-lg text-left transition-all',
                          selectedClientId === client.id
                            ? 'bg-brand-gold/20 border border-brand-gold/40'
                            : 'hover:bg-brand-smoke/50 border border-transparent',
                          !client.phone && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-10 h-10 rounded-full flex items-center justify-center',
                            selectedClientId === client.id
                              ? 'bg-brand-gold/30'
                              : 'bg-brand-smoke/50'
                          )}>
                            <User className={cn(
                              'w-5 h-5',
                              selectedClientId === client.id
                                ? 'text-brand-gold'
                                : 'text-brand-ivory/50'
                            )} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              'font-medium truncate',
                              selectedClientId === client.id
                                ? 'text-brand-gold'
                                : 'text-white'
                            )}>
                              {client.name}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-brand-ivory/50">
                              {client.phone ? (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {client.phone}
                                </span>
                              ) : (
                                <span className="text-red-400">Sem telefone</span>
                              )}
                              {client.email && (
                                <span className="truncate">{client.email}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected Client Info */}
              {selectedClient && (
                <div className="px-4 py-2 bg-brand-gold/10 border-t border-brand-gold/20">
                  <p className="text-sm text-brand-ivory/70">
                    Iniciar conversa com: <span className="text-brand-gold font-medium">{selectedClient.name}</span>
                  </p>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-brand-gold/10">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  disabled={isCreating}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleStartConversation}
                  disabled={!selectedClientId || isCreating}
                  className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Iniciando...
                    </>
                  ) : (
                    <>
                      <MessageSquarePlus className="w-4 h-4 mr-2" />
                      Iniciar Conversa
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
